import { DateInput } from "@app/shared/components/DateInput";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FlexCol } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { ChangeEvent, type FC, useState } from "react";
import { useOpenQuestionnaireModalTranslations } from "./i18n";
import { SelectQuestionnaireDropdown } from "./SelectQuestionnaireDropdown";

export const OpenQuestionnaireModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (
        questionnaireTagId: string,
        name?: string,
        deadline?: Date,
    ) => void;
    disabled?: boolean;
    disabledTooltip?: string;
}> = ({ onConfirm, open, onOpenChange, disabled, disabledTooltip }) => {
    const [selectedQuestionnaireTag, setSelectedQuestionnaireTag] = useState<{
        id: string;
        name: string;
    }>({ id: "", name: "" });
    const [questionnaireName, setQuestionnaireName] = useState<
        string | undefined
    >(undefined);
    const [deadline, setDeadline] = useState<Date | undefined>(undefined);

    const { t, keys } = useOpenQuestionnaireModalTranslations();

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuestionnaireName(e.target.value);
    };

    const handleConfirmClick = () => {
        if (!selectedQuestionnaireTag) return;
        onConfirm(selectedQuestionnaireTag.id, questionnaireName, deadline);
        setSelectedQuestionnaireTag({ id: "", name: "" });
        setQuestionnaireName(undefined);
        setDeadline(undefined);
    };

    return (
        <Modal open={open} onOpenChange={onOpenChange}>
            <Modal.Overlay scrollable>
                <Modal.Content className="w-fit min-w-[480px]">
                    <Modal.Header>
                        <Modal.Title>{t(keys.modal_title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <FlexCol gap="2">
                                <Text>
                                    {t(keys.select_questionnaire_placeholder)}
                                </Text>
                                <SelectQuestionnaireDropdown
                                    selectedQuestionnaireTag={
                                        selectedQuestionnaireTag
                                    }
                                    setSelectedQuestionnaireTag={
                                        setSelectedQuestionnaireTag
                                    }
                                />
                            </FlexCol>
                            <Textfield
                                defaultValue={selectedQuestionnaireTag.name}
                                value={questionnaireName}
                                onChange={handleNameChange}
                                placeholder={t(
                                    keys.questionnaire_name_placeholder,
                                )}
                            />
                            <DateInput
                                date={deadline}
                                setDate={setDeadline}
                                placeholder={t(keys.set_a_deadline)}
                                disablePast
                            />
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>
                            {t(keys.cancel_button)}
                        </Modal.CancelCTA>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <div>
                                    <Modal.ConfirmCTA
                                        disabled={
                                            !selectedQuestionnaireTag.id ||
                                            disabled
                                        }
                                        onClick={handleConfirmClick}
                                    >
                                        {t(keys.open_button)}
                                    </Modal.ConfirmCTA>
                                </div>
                            </Tooltip.Trigger>
                            {disabled && disabledTooltip && (
                                <Tooltip.Content>
                                    {disabledTooltip}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
